/* eslint-disable max-len */
import { isEmpty } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useGetApplicationQuery } from '../../../api/queries';
import { ApplicationStage } from '../../../api/types';
import { ApplicationDetailsExtraContext } from '../../../components/contexts/ApplicationDetailsExtraContext';
import LoadingElement from '../../../components/LoadingElement';
import { useThemeComponents } from '../../../themes';
import { getApplicationPageElement } from '../../portal/ApplicationListEndpoint/ApplicationDetailsPage';
import NotFoundPage from '../../portal/NotFoundPage';
import {
    ReferenceApplicationProvider,
    eventAdminPath,
    finderAdminPath,
} from '../../shared/ApplicationDetailsPage/standard/ApplicationTab/ReferenceApplicationField';

const AppointmentDetailsPage = () => {
    const { PageWithHeader } = useThemeComponents();
    const { id } = useParams<{ id: string }>();

    const { data, loading, refetch } = useGetApplicationQuery({ variables: { id }, fetchPolicy: 'cache-and-network' });
    const application = data?.application;

    const navigate = useNavigate();
    const { t } = useTranslation('applicationDetails');

    const extra = useMemo(() => ({ application, refetch }), [application, refetch]);

    if (loading) {
        return <LoadingElement />;
    }

    if (
        isEmpty(application) ||
        application.__typename === 'MobilityApplication' ||
        !application.stages.includes(ApplicationStage.Appointment)
    ) {
        return <NotFoundPage />;
    }

    return (
        <ApplicationDetailsExtraContext.Provider value={extra}>
            <PageWithHeader
                onBack={() => navigate('/admin/appointments')}
                title={t('applicationDetails:titles.appointment', { id: application.appointmentStage?.identifier })}
            >
                <ReferenceApplicationProvider
                    referencePaths={{
                        stages: {
                            [ApplicationStage.Appointment]: '/admin/appointments',
                            [ApplicationStage.Financing]: '/admin/applications',
                            [ApplicationStage.Insurance]: '/admin/insurances',
                            [ApplicationStage.Lead]: '/admin/leads',
                            [ApplicationStage.Reservation]: '/admin/reservations',
                        },
                        references: {
                            getEventDetail: eventAdminPath,
                            getFinderDetail: finderAdminPath,
                        },
                    }}
                >
                    {getApplicationPageElement(application, ApplicationStage.Appointment)}
                </ReferenceApplicationProvider>
            </PageWithHeader>
        </ApplicationDetailsExtraContext.Provider>
    );
};

export default AppointmentDetailsPage;
